import { useEffect, useState } from 'react'
import Split from 'react-split'
import styles from './Projektprognos.module.css'
import ProjektprognosCalculationsBar from './CalculationsBar/ProjektprognosCalculationsBar'
import ProjektprognosCashflowGrid from './MainGrids/CashflowGrid/ProjektprognosCashflowGrid'
import ProjektprognosHeader from './Headers/ProjektprognosHeader'
import { useProjektprognosEditingContext } from './context/ProjektprognosEditingContext'
import { useProjektprognosInteractionContext } from './context/ProjektprognosInteractionContext'
import { useUser } from '../../fetchData/queries/useUser'
import { usePrefetchProjektprognosSnapshots } from './fetchData/queries/snapshot/useProjektprognosSnapshots'
import { usePrefetchProjektprognosInternalCredits } from './fetchData/queries/internalCredits/useProjektprognosInternalCredits'
import { usePrefetchProjektprognosFinalAccountReport } from './fetchData/queries/reports/pdf/useProjektprognosFinalAccountReport'
import ProjektprognosPerformanceGrid from './MainGrids/PerformanceGrid/ProjektprognosPerformanceGrid'
import { ProjektprognosNotificationBars } from './SystemState/ProjektprognosNotificationBars'
import { usePrefetchProjektprognosResultCalculationReport } from './fetchData/queries/reports/pdf/useProjektprognosResultCalculationReport'
import { usePrefetchProjektprognosProfitOnContractReport } from './fetchData/queries/reports/grid/useProjektprognosProfitOnContract'
import { useUncontrolledFocus } from '@fluentui/react-components'
import { FocusTrapZone } from '@fluentui/react'
import { usePeriodReportSnapshots } from './fetchData/queries/usePeriodReportSnapshots'
import { ProjektprognosNoReportState } from './SystemState/ProjektprognosNoReportState'

const Projektprognos = () => {
  const { setEditingEnabled, setReadOnlyMode, setSelectedPeriodReport } =
    useProjektprognosEditingContext() // TODO: Remove edit/Readonly later

  const prefetchResultCalculationReport =
    usePrefetchProjektprognosResultCalculationReport()
  const prefetchFinalAccountReport =
    usePrefetchProjektprognosFinalAccountReport()
  const prefetchProfitOnContractReport =
    usePrefetchProjektprognosProfitOnContractReport()
  const prefetchSnapshots = usePrefetchProjektprognosSnapshots()
  const prefetchInternalCredits = usePrefetchProjektprognosInternalCredits()

  const user = useUser()

  const { loadProjektprognos } = useProjektprognosInteractionContext()

  const { data: periodReports, isSuccess: isPeriodReportsSuccess } =
    usePeriodReportSnapshots()
  const hasPeriodReports = (periodReports?.length ?? 0) > 0

  const attr = useUncontrolledFocus()

  const [isAccountSpecificationModalOpen, setIsAccountSpecificationModalOpen] =
    useState<boolean>(false) // TODO: Remove later
  const [modalAccountId, setModalAccountId] = useState<string>('')
  const [modalOutcomeDate, setModalOutcomeDate] = useState<string>('')
  const [isContextReset, setIsContextReset] = useState(false)

  useEffect(() => {
    if (loadProjektprognos) {
      // Clear any stale selected report from previous session to avoid flash of wrong content
      setSelectedPeriodReport(null)
      setIsContextReset(true)

      prefetchResultCalculationReport()
      prefetchFinalAccountReport()
      prefetchProfitOnContractReport()
      prefetchSnapshots()
      prefetchInternalCredits()

      const userHasWritePermission = user.data?.scopes['sheet_write'] ?? false
      setEditingEnabled(userHasWritePermission) // used to control write permission
      setReadOnlyMode(!userHasWritePermission) // used for enabling/disabling write on user interaction
    }
  }, [
    loadProjektprognos,
    prefetchFinalAccountReport,
    prefetchInternalCredits,
    prefetchProfitOnContractReport,
    prefetchResultCalculationReport,
    prefetchSnapshots,
    setEditingEnabled,
    setReadOnlyMode,
    user.data?.scopes,
  ])

  return (
    <>
      <div
        className={styles.projektprognosContainer}
        data-testid="Projektprognos"
      >
        <ProjektprognosHeader />

        <ProjektprognosNotificationBars />

        {isContextReset &&
          (isPeriodReportsSuccess && !hasPeriodReports ? (
            <ProjektprognosNoReportState />
          ) : (
            <Split
              className={styles.splitContainer}
              sizes={[60, 40]}
              minSize={0}
              expandToMin={false}
              gutterSize={5}
              gutterAlign="center"
              snapOffset={0}
              dragInterval={1}
              direction="vertical"
            >
              <FocusTrapZone
                {...attr}
                isClickableOutsideFocusTrap
                forceFocusInsideTrap={false}
              >
                <ProjektprognosCashflowGrid
                  setIsAccountSpecificationModalOpen={
                    setIsAccountSpecificationModalOpen
                  }
                  modalAccountId={modalAccountId}
                  setModalAccountId={setModalAccountId}
                  modalOutcomeDate={modalOutcomeDate}
                  setModalOutcomeDate={setModalOutcomeDate}
                />
              </FocusTrapZone>
              <Split
                className="split-horizontal"
                sizes={[60, 40]}
                minSize={0}
                expandToMin={false}
                gutterSize={5}
                gutterAlign="center"
                snapOffset={0}
                dragInterval={1}
                direction="horizontal"
              >
                <ProjektprognosPerformanceGrid
                  setIsAccountSpecificationModalOpen={
                    setIsAccountSpecificationModalOpen
                  }
                  modalAccountId={modalAccountId}
                  setModalAccountId={setModalAccountId}
                  modalOutcomeDate={modalOutcomeDate}
                  setModalOutcomeDate={setModalOutcomeDate}
                />

                <ProjektprognosCalculationsBar />
              </Split>
            </Split>
          ))}
      </div>
    </>
  )
}

export default Projektprognos
